import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { userAPI } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Resolve a picture field to a usable src or null. */
const resolvePicture = (picture) => {
  if (!picture) return null;
  if (picture.startsWith('data:') || picture.startsWith('http')) return picture;
  if (picture.startsWith('/api/')) return `${BACKEND_URL}${picture}`;
  return picture;
};

/**
 * Avatar — shows photo when available, falls back to a coloured initial circle.
 */
const Avatar = ({ name, picture, size = 'w-8 h-8', textSize = 'text-sm' }) => {
  const [imgError, setImgError] = useState(false);
  const src = resolvePicture(picture);
  const initial = (name || '?').charAt(0).toUpperCase();

  if (src && !imgError) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => setImgError(true)}
        className={`${size} rounded-full object-cover flex-shrink-0 select-none border border-slate-200 dark:border-slate-600`}
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center ${textSize} font-semibold text-slate-600 dark:text-slate-200 flex-shrink-0 select-none`}
    >
      {initial}
    </div>
  );
};

/**
 * Modal showing the full list of users who reacted to a message.
 *
 * Props:
 *   reactions     - { emoji: [userId, ...] }
 *   memberMap     - { userId: { name, picture } }
 *   currentUserId - id of the logged-in user (rows they own show "Tap to remove")
 *   msgId         - message id (passed to onReact)
 *   onReact       - (msgId, emoji) => void
 *   onClose       - () => void
 */
const ReactionDetailsPopup = ({ reactions, memberMap = {}, currentUserId, msgId, onReact, onClose }) => {
  // Extra entries fetched from the backend for user_ids not in memberMap
  const [fetchedMap, setFetchedMap] = useState({});

  // Back-button support.
  // The badge click handler (ChatMessageBubble) already called pushState before mounting us.
  // We only need to listen for popstate and suppress it when closing via UI.
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const suppressNextPopstate = React.useRef(false);

  useEffect(() => {
    const handlePopstate = () => {
      if (suppressNextPopstate.current) {
        suppressNextPopstate.current = false;
        return;
      }
      onCloseRef.current();
    };
    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, []);

  // Use this for all UI-triggered closes (X, backdrop, Tap to remove).
  // Suppresses the popstate that history.back() will fire, then calls onClose directly.
  const handleClose = React.useCallback(() => {
    suppressNextPopstate.current = true;
    window.history.back();
    onCloseRef.current();
  }, []);

  // Swipe-down to dismiss (handle + header drag targets only — avoids conflict with scroll list)
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(0);
  const onDragStart = (e) => { dragStartY.current = e.touches[0].clientY; };
  const onDragMove  = (e) => {
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const onDragEnd = () => {
    if (dragY > 80) handleClose();
    else setDragY(0);
  };
  const dragHandlers = { onTouchStart: onDragStart, onTouchMove: onDragMove, onTouchEnd: onDragEnd };

  useEffect(() => {
    const allIds = [...new Set(Object.values(reactions).flat())];
    // Fetch anyone whose picture is missing — includes users in memberMap with picture: null
    const needsFetch = allIds.filter(id => !memberMap[id]?.picture);
    if (needsFetch.length === 0) return;

    userAPI.resolve(needsFetch)
      .then(res => {
        const extra = {};
        (res.data || []).forEach(u => {
          extra[u.user_id] = { name: u.name || u.email || 'Unknown', picture: u.picture || null };
        });
        setFetchedMap(extra);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Merge: memberMap for name (authoritative), fetchedMap to fill in missing picture
  const lookup = (id) => {
    const base = memberMap[id];
    const fetched = fetchedMap[id];
    if (!base) return fetched || null;
    return { name: base.name, picture: base.picture || fetched?.picture || null };
  };

  // Build per-emoji lists
  const emojiEntries = Object.entries(reactions)
    .filter(([, users]) => users.length > 0)
    .map(([emoji, userIds]) => ({
      emoji,
      users: userIds.map(id => {
        const entry = lookup(id);
        return {
          id,
          name: entry?.name || null,
          picture: entry?.picture || null,
          loading: !entry,
          isMe: id === currentUserId,
        };
      }),
    }));

  const allRows = emojiEntries.flatMap(({ emoji, users }) =>
    users.map(u => ({ ...u, emoji }))
  );

  const [activeTab, setActiveTab] = useState('all');

  const displayed =
    activeTab === 'all'
      ? allRows
      : (emojiEntries.find(e => e.emoji === activeTab)?.users ?? []).map(u => ({
          ...u,
          emoji: activeTab,
        }));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

      {/* Sheet */}
      <div
        className="relative bg-white dark:bg-slate-800 w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="reaction-details-popup"
        style={{
          transform: `translateY(${dragY}px)`,
          transition: dragY === 0 ? 'transform 0.2s ease-out' : 'none',
        }}
      >
        {/* Handle (mobile) — drag here to dismiss */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing" {...dragHandlers}>
          <div className="w-10 h-1 bg-slate-300 dark:bg-slate-600 rounded-full" />
        </div>

        {/* Header — also draggable */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100 dark:border-slate-700" {...dragHandlers}>
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Reactions</h3>
          <button
            onClick={handleClose}
            className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            data-testid="reaction-details-close"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Emoji tabs — only shown when more than one emoji has reactions */}
        {emojiEntries.length > 1 && (
          <div className="flex gap-1 px-3 pt-2 pb-1 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                activeTab === 'all'
                  ? 'bg-slate-800 dark:bg-slate-100 text-white dark:text-slate-900'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              All&nbsp;{allRows.length}
            </button>
            {emojiEntries.map(({ emoji, users }) => (
              <button
                key={emoji}
                onClick={() => setActiveTab(emoji)}
                className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  activeTab === emoji
                    ? 'bg-slate-800 dark:bg-slate-100 text-white dark:text-slate-900'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                {emoji}&nbsp;{users.length}
              </button>
            ))}
          </div>
        )}

        {/* User list */}
        <div className="max-h-64 overflow-y-auto py-1">
          {displayed.map(({ id, name, picture, loading, isMe, emoji }) => (
            <div
              key={`${id}-${emoji}`}
              onClick={isMe && onReact ? () => { onReact(msgId, emoji); handleClose(); } : undefined}
              className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                isMe && onReact
                  ? 'cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 active:bg-red-100 dark:active:bg-red-900/30'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
              }`}
            >
              {loading ? (
                <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 animate-pulse flex-shrink-0" />
              ) : (
                <Avatar name={name || '?'} picture={picture} />
              )}
              <span
                className={`flex-1 text-sm font-medium truncate ${
                  loading
                    ? 'text-slate-300 dark:text-slate-600'
                    : 'text-slate-800 dark:text-slate-100'
                }`}
              >
                {loading ? '···' : (name || id)}
              </span>
              {isMe && onReact ? (
                <span className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-xl">{emoji}</span>
                  <span className="text-xs text-red-400 dark:text-red-400 font-medium">Tap to remove</span>
                </span>
              ) : (
                <span className="text-xl flex-shrink-0">{emoji}</span>
              )}
            </div>
          ))}
        </div>

        {/* Bottom safe-area padding on mobile */}
        <div className="sm:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 8px)' }} />
      </div>
    </div>
  );
};

export default ReactionDetailsPopup;
